import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
    getTrainerWeeklyNotes,
    upsertTrainerWeeklyNote,
    deleteTrainerWeeklyNote
} from '../api/trainerWeeklyNotesApi';
import { getTrainerVacations } from '../api/trainerVacationApi';

const WeekRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: '#fafafa',
    overflow: 'auto',
    position: 'relative',
    '&:not(:last-of-type)::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '1px',
        backgroundColor: '#e8e8e8'
    }
}));

const TrainerNoteItem = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5),
    borderBottom: '1px solid #e8e8e8',
    '&:last-child': {
        borderBottom: 'none'
    }
}));

const EditableNoteField = styled(Box)(({ theme }) => ({
    minHeight: '40px',
    maxHeight: '80px',
    overflowY: 'auto',
    padding: '4px 8px',
    border: '1px solid #AAAAAA',
    borderRadius: '4px',
    fontSize: '13px',
    lineHeight: '1.4',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    '&:focus': {
        outline: 'none',
        borderColor: '#1976d2',
        borderWidth: '2px'
    },
    '&:empty:before': {
        content: 'attr(data-placeholder)',
        color: '#999',
        fontSize: '12px'
    }
}));

export default function TrainerWeeklyNotes({ 
    affiliate, 
    trainers, 
    visibleWeeks = [],
    isMobile 
}) {
    const [notesByWeek, setNotesByWeek] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [savingNotes, setSavingNotes] = useState({});
    const [editedNotes, setEditedNotes] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [editingNotes, setEditingNotes] = useState({});
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [weekHeights, setWeekHeights] = useState({});
    const [calendarHeaderHeight, setCalendarHeaderHeight] = useState(0);
    const [vacations, setVacations] = useState([]);
    
    const noteRefs = useRef({});
    const weekRowRefs = useRef({});
    const notesHeaderRef = useRef(null);

    const userRole = localStorage.getItem('role');
    const isAffiliate = userRole === 'affiliate';
    const isTrainer = userRole === 'trainer';
    
    const currentUserTrainerId = currentUserId
        ? trainers.find(t => t.trainerId === currentUserId)?.trainerId 
        : null;
    
    // Calculate the Monday of a given date
    const getMonday = useCallback((date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        const monday = new Date(d.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    }, []);
    
    // Fetch current user ID
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const token = localStorage.getItem('token');
                // API_URL imported from config
                const response = await fetch(`${API_URL}/auth/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = await response.json();
                if (data.id) {
                    setCurrentUserId(data.id);
                }
            } catch (err) {
                console.error('❌ Failed to fetch current user:', err);
            }
        };
        
        if (isTrainer && trainers.length > 0) {
            fetchCurrentUser();
        }
    }, [isTrainer, trainers.length]);

    // Create a stable key from visible weeks to detect actual changes
    const visibleWeeksKey = useMemo(() => {
        return visibleWeeks.map(week => week.toISOString()).join(',');
    }, [visibleWeeks]);

    // Load vacations for the visible weeks' years
    useEffect(() => {
        const loadVacations = async () => {
            if (!affiliate?.id || visibleWeeks.length === 0) return;
            
            try {
                // Get unique years from visible weeks
                const years = [...new Set(visibleWeeks.map(week => new Date(week).getFullYear()))];
                console.log('📅 Loading vacations for years:', years);
                
                // Load vacations for all years
                const allVacations = await Promise.all(
                    years.map(year => getTrainerVacations(affiliate.id, year))
                );
                
                // Flatten the array of arrays
                const flatVacations = allVacations.flat();
                console.log('🏖️ Loaded vacations:', flatVacations.map(v => ({
                    id: v.id,
                    trainer: v.trainer?.fullName,
                    startDate: v.startDate,
                    endDate: v.endDate,
                    startDateParsed: new Date(v.startDate).toISOString(),
                    endDateParsed: new Date(v.endDate).toISOString()
                })));
                setVacations(flatVacations);
            } catch (err) {
                console.error('❌ Failed to load vacations:', err);
            }
        };
        
        loadVacations();
    }, [affiliate?.id, visibleWeeksKey]);

    useEffect(() => {
        if (affiliate?.id && visibleWeeks.length > 0) {
            loadAllWeeksNotes();
        }
    }, [affiliate?.id, visibleWeeksKey]);

    const loadAllWeeksNotes = async () => {
        try {
            setLoading(true);
            const notesData = {};
            const initialEdited = {};
            
            await Promise.all(
                visibleWeeks.map(async (weekStart) => {
                    const weekKey = weekStart.toISOString();
                    const data = await getTrainerWeeklyNotes(affiliate.id, weekStart);
                    notesData[weekKey] = data;
                    
                    data.forEach(note => {
                        const noteKey = `${weekKey}-${note.trainerId}`;
                        initialEdited[noteKey] = note.note;
                    });
                })
            );
            
            setNotesByWeek(notesData);
            setEditedNotes(initialEdited);
            setEditingNotes({});
            setSavingNotes({});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Object.keys(notesByWeek).length > 0) {
            visibleWeeks.forEach(weekStart => {
                const weekKey = weekStart.toISOString();
                trainers.forEach(trainer => {
                    const noteKey = `${weekKey}-${trainer.trainerId}`;
                    const ref = noteRefs.current[noteKey];
                    if (ref && document.activeElement !== ref) {
                        const noteContent = editedNotes[noteKey] || '';
                        if (ref.innerHTML !== noteContent) {
                            ref.innerHTML = noteContent;
                        }
                    }
                });
            });
        }
    }, [visibleWeeks, trainers, editedNotes, notesByWeek]);

    // Measure calendar headers and week row heights (only on desktop)
    useEffect(() => {
        if (!loading && visibleWeeks.length > 0 && !isMobile) {
            const measureHeights = () => {
                // Measure calendar toolbar + day headers + scroller padding
                const toolbar = document.querySelector('.fc-toolbar-chunk');
                const dayHeaders = document.querySelector('.fc-col-header');
                const scrollerTop = document.querySelector('.fc-scroller-harness');
                
                let totalHeaderHeight = 0;
                
                if (toolbar) {
                    const toolbarParent = toolbar.closest('.fc-toolbar');
                    if (toolbarParent) {
                        totalHeaderHeight += toolbarParent.offsetHeight;
                    }
                }
                if (dayHeaders) {
                    totalHeaderHeight += dayHeaders.offsetHeight;
                }
                
                // Add any top margin/padding from scroller
                if (scrollerTop) {
                    const computedStyle = window.getComputedStyle(scrollerTop);
                    const marginTop = parseInt(computedStyle.marginTop) || 0;
                    totalHeaderHeight += marginTop;
                }
                
                setCalendarHeaderHeight(totalHeaderHeight);
                
                // Set notes header height to match
                if (notesHeaderRef.current && totalHeaderHeight > 0) {
                    notesHeaderRef.current.style.height = `${totalHeaderHeight}px`;
                    notesHeaderRef.current.style.minHeight = `${totalHeaderHeight}px`;
                    notesHeaderRef.current.style.maxHeight = `${totalHeaderHeight}px`;
                }
                
                // Measure week rows
                const calendarRows = document.querySelectorAll('.fc-daygrid-body tbody tr[role="row"]');
                const heights = {};
                
                // Get the first day of each week from calendar cells
                calendarRows.forEach((row, index) => {
                    // Get the first day cell in this row (Monday)
                    const firstDayCell = row.querySelector('.fc-day[data-date]');
                    if (firstDayCell) {
                        const dateStr = firstDayCell.getAttribute('data-date');
                        if (dateStr) {
                            const weekStart = getMonday(new Date(dateStr));
                            const weekKey = weekStart.toISOString();
                            const height = row.offsetHeight;
                            heights[weekKey] = height;
                            
                            // Apply height to note row
                            const noteRow = weekRowRefs.current[weekKey];
                            if (noteRow) {
                                noteRow.style.boxSizing = 'border-box';
                                noteRow.style.height = `${height}px`;
                                noteRow.style.minHeight = `${height}px`;
                                noteRow.style.maxHeight = `${height}px`;
                            }
                        }
                    } else if (visibleWeeks[index]) {
                        // Fallback to visibleWeeks if data-date is not available
                        const weekKey = visibleWeeks[index].toISOString();
                        const height = row.offsetHeight;
                        heights[weekKey] = height;
                        
                        const noteRow = weekRowRefs.current[weekKey];
                        if (noteRow) {
                            noteRow.style.boxSizing = 'border-box';
                            noteRow.style.height = `${height}px`;
                            noteRow.style.minHeight = `${height}px`;
                            noteRow.style.maxHeight = `${height}px`;
                        }
                    }
                });
                
                setWeekHeights(heights);
            };

            // Measure after short delays to ensure calendar is fully rendered
            const timer1 = setTimeout(measureHeights, 100);
            const timer2 = setTimeout(measureHeights, 500);
            const timer3 = setTimeout(measureHeights, 1000);
            const timer4 = setTimeout(measureHeights, 2000);
            
            // Re-measure on window resize
            window.addEventListener('resize', measureHeights);
            
            // Use MutationObserver to detect calendar changes
            const calendar = document.querySelector('.fc');
            let observer;
            if (calendar) {
                observer = new MutationObserver(() => {
                    setTimeout(measureHeights, 50);
                });
                observer.observe(calendar, { 
                    childList: true, 
                    subtree: true
                });
            }
            
            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
                clearTimeout(timer3);
                clearTimeout(timer4);
                window.removeEventListener('resize', measureHeights);
                if (observer) observer.disconnect();
            };
        }
    }, [loading, visibleWeeks, notesByWeek, isMobile]);

    const handleNoteInput = useCallback((weekKey, trainerId, e) => {
        const value = e.currentTarget.innerHTML;
        const noteKey = `${weekKey}-${trainerId}`;
        setEditedNotes(prev => ({
            ...prev,
            [noteKey]: value
        }));
    }, []);

    const handleEditNote = (weekKey, trainerId) => {
        const noteKey = `${weekKey}-${trainerId}`;
        if (!canEditTrainerNote(trainerId)) return;
        setEditingNotes(prev => ({ ...prev, [noteKey]: true }));
    };

    const handleCancelEdit = (weekKey, trainerId) => {
        const noteKey = `${weekKey}-${trainerId}`;
        setEditingNotes(prev => ({ ...prev, [noteKey]: false }));
        const weekNotes = notesByWeek[weekKey] || [];
        const existingNote = weekNotes.find(n => n.trainerId === trainerId);
        setEditedNotes(prev => ({
            ...prev,
            [noteKey]: existingNote?.note || ''
        }));
    };

    const handleSaveNote = async (weekStart, weekKey, trainerId) => {
        const noteKey = `${weekKey}-${trainerId}`;
        try {
            setSavingNotes(prev => ({ ...prev, [noteKey]: true }));
            const noteContent = editedNotes[noteKey] || '';
            
            await upsertTrainerWeeklyNote(
                trainerId,
                affiliate.id,
                weekStart,
                noteContent
            );
            
            await loadAllWeeksNotes();
            setEditingNotes(prev => ({ ...prev, [noteKey]: false }));
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingNotes(prev => ({ ...prev, [noteKey]: false }));
        }
    };

    const handleDeleteClick = (note) => {
        if (!canEditTrainerNote(note.trainerId)) {
            setError("You don't have permission to delete this note");
            return;
        }
        setNoteToDelete(note);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!noteToDelete) return;
        
        if (!canEditTrainerNote(noteToDelete.trainerId)) {
            setError("You don't have permission to delete this note");
            setDeleteConfirmOpen(false);
            setNoteToDelete(null);
            return;
        }

        try {
            setIsDeleting(true);
            await deleteTrainerWeeklyNote(noteToDelete.id);
            setDeleteConfirmOpen(false);
            setNoteToDelete(null);
            await loadAllWeeksNotes();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const getWeekDateRange = (weekStart) => {
        const startDate = new Date(weekStart);
        const endDate = new Date(weekStart);
        endDate.setDate(endDate.getDate() + 6);
        
        return `${startDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short' 
        })} - ${endDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short'
        })}`;
    };

    const hasNoteChanged = (weekKey, trainerId) => {
        const noteKey = `${weekKey}-${trainerId}`;
        const weekNotes = notesByWeek[weekKey] || [];
        const existingNote = weekNotes.find(n => n.trainerId === trainerId);
        const currentNote = editedNotes[noteKey] || '';
        const originalNote = existingNote?.note || '';
        return currentNote !== originalNote;
    };

    const canEditTrainerNote = (trainerId) => {
        if (isAffiliate) return true;
        if (isTrainer) return trainerId === currentUserTrainerId;
        return false;
    };

    // Check if a trainer has vacation during a specific week
    const getVacationForWeek = (trainerId, weekStart) => {
        // Get week start and end as date-only strings (yyyy-mm-dd) for comparison
        const weekStartDate = new Date(weekStart);
        const weekStartStr = weekStartDate.toISOString().split('T')[0];
        
        const weekEndDate = new Date(weekStart);
        weekEndDate.setDate(weekEndDate.getDate() + 6);
        const weekEndStr = weekEndDate.toISOString().split('T')[0];
        
        // Find vacations that overlap with this week
        const overlappingVacations = vacations.filter(vacation => {
            if (vacation.trainerId !== trainerId) return false;
            
            // Get vacation dates as date-only strings
            const vacStartStr = new Date(vacation.startDate).toISOString().split('T')[0];
            const vacEndStr = new Date(vacation.endDate).toISOString().split('T')[0];
            
            // Debug logging
            console.log(`🔍 Checking trainer ${trainerId}:`);
            console.log(`   Week: ${weekStartStr} to ${weekEndStr}`);
            console.log(`   Vacation: ${vacStartStr} to ${vacEndStr}`);
            console.log(`   Overlap: ${vacStartStr} <= ${weekEndStr} && ${vacEndStr} >= ${weekStartStr} = ${vacStartStr <= weekEndStr && vacEndStr >= weekStartStr}`);
            
            // Check if vacation overlaps with week using string comparison (yyyy-mm-dd format)
            // Vacation overlaps if: vacation starts on or before week end AND vacation ends on or after week start
            return (vacStartStr <= weekEndStr && vacEndStr >= weekStartStr);
        });
        
        if (overlappingVacations.length === 0) return null;
        
        // Return formatted vacation period
        const vacation = overlappingVacations[0]; // Take first if multiple
        const vacStart = new Date(vacation.startDate);
        const vacEnd = new Date(vacation.endDate);
        
        // Get trainer name from vacation object or trainers array
        const trainerName = vacation.trainer?.fullName || 
                          trainers.find(t => t.trainerId === trainerId)?.fullName || 
                          'Trainer';
        
        const formatDate = (date) => {
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short'
            });
        };
        
        return `🏖️ ${trainerName} - Vacation: ${formatDate(vacStart)} - ${formatDate(vacEnd)}`;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header - Aligns with calendar header on desktop */}
            <Box 
                ref={notesHeaderRef}
                sx={{ 
                    mb: 0,
                    borderBottom: '1px solid #e0e0e0',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    ...(isMobile && {
                        height: 'auto !important',
                        minHeight: 'auto !important',
                        maxHeight: 'none !important',
                        py: 2
                    })
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    fontWeight="bold" 
                    sx={{ 
                        color: '#1976d2',
                    }}
                >
                    Notes
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {trainers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No trainers found.
                </Typography>
            ) : visibleWeeks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No weeks visible.
                </Typography>
            ) : (
                <Box>
                    {visibleWeeks.map((weekStart) => {
                        const weekKey = weekStart.toISOString();
                        const weekNotes = notesByWeek[weekKey] || [];
                        
                        // Filter trainers based on role and note existence
                        const visibleTrainers = trainers.filter(trainer => {
                            const existingNote = weekNotes.find(n => n.trainerId === trainer.trainerId);
                            
                            // Always show if there's an existing note
                            if (existingNote) return true;
                            
                            // Show if trainer has vacation this week
                            const hasVacation = getVacationForWeek(trainer.trainerId, weekStart);
                            if (hasVacation) return true;
                            
                            // For trainers, show their own row even without a note (so they can create one)
                            if (isTrainer && trainer.trainerId === currentUserTrainerId) return true;
                            
                            // For affiliates (admin), only show trainers with notes or vacation
                            // This hides empty note entries
                            return false;
                        });
                        
                        // For affiliate view, always show the week row (even if no trainers have notes/vacation)
                        // This ensures every week has a notes table behind it
                        // For trainer view, only show if there are visible trainers
                        if (!isAffiliate && visibleTrainers.length === 0) {
                            return null;
                        }
                        
                        return (
                            <WeekRow 
                                key={weekKey}
                                ref={el => weekRowRefs.current[weekKey] = el}
                                sx={isMobile ? { 
                                    height: 'auto !important',
                                    minHeight: 'auto !important',
                                    maxHeight: 'none !important'
                                } : {}}
                            >
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        display: 'block',
                                        mb: 1, 
                                        color: '#666',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {getWeekDateRange(weekStart)}
                                </Typography>
                                
                                {visibleTrainers.map((trainer) => {
                                    const noteKey = `${weekKey}-${trainer.trainerId}`;
                                    const existingNote = weekNotes.find(n => n.trainerId === trainer.trainerId);
                                    const canEdit = canEditTrainerNote(trainer.trainerId);
                                    const isEditingThisNote = editingNotes[noteKey] || false;
                                    const isSaving = savingNotes[noteKey] || false;
                                    const hasChanged = hasNoteChanged(weekKey, trainer.trainerId);
                                    
                                    return (
                                        <TrainerNoteItem key={noteKey}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'flex-start',
                                                mb: 0.5
                                            }}>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="bold"
                                                    sx={{ 
                                                        color: '#444',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    {trainer.fullName}
                                                    {isTrainer && trainer.trainerId === currentUserTrainerId && (
                                                        <Typography 
                                                            component="span" 
                                                            variant="caption" 
                                                            sx={{ ml: 0.5, color: 'text.secondary' }}
                                                        >
                                                            (You)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                
                                                {canEdit && (
                                                    <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                        {!isEditingThisNote ? (
                                                            <>
                                                                <Tooltip title="Edit">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditNote(weekKey, trainer.trainerId)}
                                                                        sx={{ p: 0.3 }}
                                                                    >
                                                                        <EditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {existingNote && (
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDeleteClick(existingNote)}
                                                                            sx={{ p: 0.3 }}
                                                                            color="error"
                                                                        >
                                                                            <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Tooltip title={hasChanged ? "Save" : "No changes"}>
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleSaveNote(weekStart, weekKey, trainer.trainerId)}
                                                                            disabled={!hasChanged || isSaving}
                                                                            sx={{ p: 0.3 }}
                                                                            color="success"
                                                                        >
                                                                            {isSaving ? <CircularProgress size={12} /> : <SaveIcon fontSize="small" sx={{ fontSize: '1rem' }} />}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <Tooltip title="Cancel">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleCancelEdit(weekKey, trainer.trainerId)}
                                                                        disabled={isSaving}
                                                                        sx={{ p: 0.3 }}
                                                                        color="error"
                                                                    >
                                                                        <CloseIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                            
                                            <EditableNoteField
                                                ref={el => noteRefs.current[noteKey] = el}
                                                contentEditable={isEditingThisNote}
                                                suppressContentEditableWarning
                                                onInput={(e) => handleNoteInput(weekKey, trainer.trainerId, e)}
                                                data-placeholder={
                                                    isEditingThisNote 
                                                        ? "Add availability notes..." 
                                                        : "No notes"
                                                }
                                            />
                                            
                                            {/* Display vacation info if trainer has vacation this week */}
                                            {(() => {
                                                const vacationInfo = getVacationForWeek(trainer.trainerId, weekStart);
                                                if (vacationInfo) {
                                                    return (
                                                        <Box sx={{ 
                                                            mt: 0.5, 
                                                            p: 0.5, 
                                                            backgroundColor: '#fff3e0',
                                                            borderRadius: 1,
                                                            border: '1px solid #ff9800'
                                                        }}>
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    color: '#e65100',
                                                                    fontWeight: 'bold',
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {vacationInfo}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </TrainerNoteItem>
                                    );
                                })}
                            </WeekRow>
                        );
                    })}
                </Box>
            )}

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Delete Note</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this note?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        color="error" 
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} /> : null}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
